// Copied into your app by RapidRailsUI (bin/rails rui:update).
// Don't edit this copy: the next update overwrites it. Fix it in the rapid_rails_ui gem.
import { Controller } from "@hotwired/stimulus"
import { placeMenu, resetPlacementClasses } from "./shared/menu_placement"

/**
 * Menu Controller
 * @gem rapid_rails_ui
 * @version 0.85.1
 * @updated 2026-10-14
 *
 * Core menu/dropdown functionality: open, close, positioning, and click outside.
 * Designed to be composed with shared controllers for additional behaviors.
 *
 * Features:
 * - Smart positioning with collision detection
 * - Auto-flips horizontally when near viewport edges
 * - Auto-flips vertically when near viewport bottom
 * - Turbo-compatible link navigation
 * - Keyboard navigation support
 *
 * Composition Example:
 *   <div data-controller="menu keyboard search"
 *        data-action="keyboard:escape->menu#close"
 *        data-keyboard-selector-value="[role='menuitem']">
 *
 * Targets:
 *   - trigger: Button that opens the menu
 *   - menu: The dropdown menu container
 *   - buttonText: Text span in trigger (for selection mode)
 *   - chevron: Chevron icon to rotate
 *
 * Values:
 *   - open: Current open state
 *   - trigger: "click" or "hover"
 *   - disabled: Disabled state
 *   - selectionMode: Whether selecting updates trigger text
 *   - promptText: Default text when no selection
 *   - placement: "top", "right", "bottom", "left"
 */
export default class extends Controller {
  static targets = ["trigger", "menu", "buttonText", "chevron"]

  static values = {
    open: { type: Boolean, default: false },
    trigger: { type: String, default: "click" },
    disabled: { type: Boolean, default: false },
    selectionMode: { type: Boolean, default: false },
    promptText: { type: String, default: "Select an option" },
    placement: { type: String, default: "bottom" },
    // "fixed" places the open menu against the viewport (JS sets left/top), so
    // a scrolling or overflow-hidden ancestor does not clip it. rui_dropdown
    // opts in; "absolute" keeps the CSS placement classes (combobox).
    strategy: { type: String, default: "absolute" }
  }

  // CSS classes
  static CLASSES = {
    chevronOpen: "rotate-180",
    menuHidden: "hidden"
  }

  // Configured placement classes. Must match Dropdown::Styles placement map
  // (dropdown/styles.rb), which renders the initial state server-side.
  static PLACEMENT_CLASSES = {
    top: ["bottom-full", "mb-2"],
    right: ["left-full", "ml-2", "top-0"],
    bottom: ["top-full", "mt-2"],
    left: ["right-full", "mr-2", "top-0"]
  }

  // Every class collision handling may add. All are cleared on close so a
  // flipped placement never survives into the next open. RRUI-019.
  static POSITION_CLASSES = [
    "left-0", "right-0",
    "left-full", "right-full", "top-full", "bottom-full",
    "mt-2", "mb-2", "ml-2", "mr-2", "top-0"
  ]

  // Timing constants
  static HOVER_OPEN_DELAY = 150
  static HOVER_CLOSE_DELAY = 200

  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================

  connect() {
    this._close()
    this._boundHandlers = {}
    this._hoverTimeout = null

    if (this.triggerValue === "hover") {
      this._setupHoverBehavior()
    }

    // Close when keyboard focus leaves the dropdown (Tab to the next nav
    // item). Without this, tabbing across a navbar left every menu open.
    // Focus moving into the menu, or to nowhere (window blur), keeps it open.
    this._focusOutHandler = (event) => {
      const next = event.relatedTarget
      if (this.openValue && next && !this.element.contains(next)) this.close()
    }
    this.element.addEventListener("focusout", this._focusOutHandler)
  }

  disconnect() {
    this._stopFollowing()
    this._clearHoverTimeout()
    this._cleanupHoverListeners()
    if (this._focusOutHandler) this.element.removeEventListener("focusout", this._focusOutHandler)
  }

  openValueChanged() {
    this.openValue ? this._open() : this._close()
  }

  // ==========================================================================
  // PUBLIC ACTIONS
  // ==========================================================================

  toggle(event) {
    if (event) event.preventDefault()
    if (this.disabledValue) return

    // Hover menus: a click on the trigger while the pointer is on it opens (or
    // keeps open) the menu hover just opened, instead of closing it. Touch
    // devices send no mouseenter before the first tap, so they still toggle.
    if (this.triggerValue === "hover" && this._mouseIsOverTrigger) {
      this._clearHoverTimeout()
      this.openValue = true
      return
    }

    this.openValue = !this.openValue
  }

  close() {
    this.openValue = false
  }

  /**
   * Close the menu and return focus to the trigger button.
   *
   * WAI-ARIA APG requires that dismissing a menu via Escape returns focus
   * to the element that opened it - `close()` alone only hides the menu,
   * leaving focus stranded wherever it was inside the (now hidden) menu.
   * Bound to keydown.esc instead of `close` directly (see menu_attributes
   * in renderers.rb and the composed action strings in combobox).
   */
  closeOnEscape(event) {
    if (event) event.preventDefault()
    this.close()
    this.triggerTarget.focus({ preventScroll: true })
  }

  open() {
    if (!this.disabledValue) {
      this.openValue = true
    }
  }

  clickOutside(event) {
    if (this.openValue && !this.element.contains(event.target)) {
      this.close()
    }
  }

  // ==========================================================================
  // KEYBOARD NAVIGATION
  // ==========================================================================

  /**
   * Navigate to next menu item (down arrow)
   */
  navigateDown(event) {
    event.preventDefault()
    this._navigate(1)
  }

  /**
   * Navigate to previous menu item (up arrow)
   */
  navigateUp(event) {
    event.preventDefault()
    this._navigate(-1)
  }

  /**
   * Navigate to first menu item (Home key)
   */
  navigateFirst(event) {
    event.preventDefault()
    const items = this._getNavigableItems()
    if (items.length > 0) {
      items[0].focus({ preventScroll: true })
    }
  }

  /**
   * Navigate to last menu item (End key)
   */
  navigateLast(event) {
    event.preventDefault()
    const items = this._getNavigableItems()
    if (items.length > 0) {
      items[items.length - 1].focus({ preventScroll: true })
    }
  }

  /**
   * Handle item selection
   */
  selectItem(event) {
    const item = event.currentTarget
    const isLink = item.tagName === 'A' && item.href

    // For links: allow Turbo to handle navigation
    // For buttons/selection: prevent default and stop propagation
    if (isLink && !this.selectionModeValue) {
      // Link navigation - just close menu and let event bubble to Turbo
      this.close()
      return
    }

    // Button or selection mode - prevent default behavior
    event.preventDefault()
    event.stopPropagation()

    const value = item.dataset.value
    const text = item.dataset.text || item.textContent.trim()

    if (this.selectionModeValue) {
      const hiddenInput = this.element.querySelector('input[type="hidden"]')
      if (hiddenInput) {
        hiddenInput.value = value
        this._updateButtonText(value === "" || value === null ? this.promptTextValue : text)
      }
    }

    this.close()
  }

  /**
   * Toggle checkbox without closing menu
   */
  toggleCheckbox(event) {
    event.stopPropagation()

    if (event.type === "keydown") {
      event.preventDefault()
      const checkbox = event.currentTarget.querySelector('input[type="checkbox"]')
      if (checkbox && !checkbox.disabled) {
        checkbox.checked = !checkbox.checked
        checkbox.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }

    const label = event.currentTarget
    const checkbox = label.querySelector('input[type="checkbox"]')
    if (checkbox) {
      label.setAttribute("aria-checked", checkbox.checked.toString())
    }
  }

  /**
   * Toggle radio without closing menu
   */
  toggleRadio(event) {
    event.stopPropagation()

    if (event.type === "keydown") {
      event.preventDefault()
      const radio = event.currentTarget.querySelector('input[type="radio"]')
      if (radio && !radio.disabled) {
        radio.checked = true
        radio.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }

    const currentRadio = event.currentTarget.querySelector('input[type="radio"]')
    if (currentRadio?.name) {
      const allRadios = this.menuTarget.querySelectorAll(`input[type="radio"][name="${currentRadio.name}"]`)
      allRadios.forEach(radio => {
        const label = radio.closest("label")
        if (label) label.setAttribute("aria-checked", radio.checked.toString())
      })
    }
  }

  // ==========================================================================
  // PRIVATE: MENU STATE
  // ==========================================================================

  _open() {
    this.menuTarget.classList.remove(this.constructor.CLASSES.menuHidden)
    this.triggerTarget.setAttribute("aria-expanded", "true")

    if (this.hasChevronTarget) {
      this.chevronTarget.classList.add(this.constructor.CLASSES.chevronOpen)
    }

    this._adjustMenuWidth()

    // Focus the menu so it can receive keyboard events
    // Use requestAnimationFrame to ensure DOM is painted first
    // Use preventScroll to avoid browser auto-scrolling to the focused element
    requestAnimationFrame(() => {
      if (this.strategyValue === "fixed") {
        this._placeFixed()
        this._startFollowing()
      } else {
        this._adjustMenuPosition()
      }
      this.menuTarget.focus({ preventScroll: true })
      this.dispatch("opened", { detail: {} })
    })
  }

  _close() {
    this.menuTarget.classList.add(this.constructor.CLASSES.menuHidden)
    this.triggerTarget.setAttribute("aria-expanded", "false")

    if (this.hasChevronTarget) {
      this.chevronTarget.classList.remove(this.constructor.CLASSES.chevronOpen)
    }

    // Reset positioning classes for next open
    this._stopFollowing()
    this._resetMenuPosition()

    this.dispatch("closed", { detail: {} })
  }

  /**
   * Reset menu to original placement (remove dynamic positioning classes)
   */
  _resetMenuPosition() {
    const menu = this.menuTarget
    const { PLACEMENT_CLASSES, POSITION_CLASSES } = this.constructor

    // Clear the complete placement set (flipped sides, edges, margins), then
    // restore exactly the configured placement. Removing only left-0/right-0
    // left contradictory pairs such as top-full + bottom-full. RRUI-019.
    // The first reset runs in connect, so this is the server-rendered state
    if (!this._serverMenuClasses) this._serverMenuClasses = [...menu.classList]
    const configured = PLACEMENT_CLASSES[this.placementValue] || PLACEMENT_CLASSES.bottom
    menu.classList.remove(...POSITION_CLASSES)
    menu.classList.add(...resetPlacementClasses(this.placementValue, configured, this._serverMenuClasses))
  }

  /**
   * Fixed strategy: measure the trigger and menu, let placeMenu decide, then
   * write left/top. The width is locked first so w-full and shrink-to-fit
   * menus keep the size they had while absolute. A transformed ancestor
   * (backdrop-blur, transform) makes fixed relative to it, so the menu's
   * offset at left/top 0 is subtracted as that ancestor's origin.
   */
  _placeFixed() {
    const menu = this.menuTarget
    if (!menu.style.position) {
      menu.style.width = `${menu.getBoundingClientRect().width}px`
      menu.classList.remove(...this.constructor.POSITION_CLASSES)
      menu.style.position = "fixed"
      menu.style.margin = "0"
    }
    menu.style.left = "0px"
    menu.style.top = "0px"
    const origin = menu.getBoundingClientRect()
    const { left, top } = placeMenu(
      this.triggerTarget.getBoundingClientRect(),
      { width: origin.width, height: origin.height },
      { viewportWidth: document.documentElement.clientWidth, viewportHeight: window.innerHeight },
      this.placementValue
    )
    menu.style.left = `${left - origin.left}px`
    menu.style.top = `${top - origin.top}px`
  }

  // While open, follow the trigger through any scroll (capture catches
  // scrolling containers too) and window resize, once per frame.
  _startFollowing() {
    if (this._follow) return
    this._follow = () => {
      if (this._followFrame) return
      this._followFrame = requestAnimationFrame(() => {
        this._followFrame = null
        if (this.openValue) this._placeFixed()
      })
    }
    window.addEventListener("scroll", this._follow, { capture: true, passive: true })
    window.addEventListener("resize", this._follow, { passive: true })
  }

  _stopFollowing() {
    if (this._follow) {
      window.removeEventListener("scroll", this._follow, { capture: true })
      window.removeEventListener("resize", this._follow)
      this._follow = null
    }
    if (this._followFrame) cancelAnimationFrame(this._followFrame)
    this._followFrame = null
    if (this.hasMenuTarget) {
      for (const prop of ["position", "left", "top", "width", "margin"]) this.menuTarget.style[prop] = ""
    }
  }

  _adjustMenuWidth() {
    const triggerWidth = this.triggerTarget.offsetWidth
    this.menuTarget.style.minWidth = `${triggerWidth}px`
  }

  /**
   * Smart positioning: adjust menu position to avoid viewport overflow
   * Flips horizontal (left/right) and vertical (top/bottom) as needed
   */
  _adjustMenuPosition() {
    const menu = this.menuTarget
    const trigger = this.triggerTarget
    const placement = this.placementValue

    // Get viewport dimensions
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    // Get menu and trigger rectangles
    const menuRect = menu.getBoundingClientRect()
    const triggerRect = trigger.getBoundingClientRect()

    // Remove any previous positioning classes
    menu.classList.remove('left-0', 'right-0', 'left-full', 'right-full', 'top-full', 'bottom-full')

    // Check horizontal overflow
    let horizontalPlacement = placement
    if (placement === 'bottom' || placement === 'top') {
      // For bottom/top placements, check if menu overflows right edge
      const overflowsRight = menuRect.right > viewportWidth - 8 // 8px buffer
      const overflowsLeft = menuRect.left < 8

      if (overflowsRight && !overflowsLeft) {
        // Menu overflows right - align right edges
        menu.classList.add('right-0')
      } else {
        // Default: align left edges
        menu.classList.add('left-0')
      }
    } else if (placement === 'right') {
      // Check if right placement would overflow
      if (menuRect.right > viewportWidth - 8) {
        // Flip to left
        menu.classList.remove('left-full', 'ml-2')
        menu.classList.add('right-full', 'mr-2')
        horizontalPlacement = 'left'
      } else {
        menu.classList.add('left-full')
      }
    } else if (placement === 'left') {
      // Check if left placement would overflow
      if (menuRect.left < 8) {
        // Flip to right
        menu.classList.remove('right-full', 'mr-2')
        menu.classList.add('left-full', 'ml-2')
        horizontalPlacement = 'right'
      } else {
        menu.classList.add('right-full')
      }
    }

    // Check vertical overflow
    if (placement === 'bottom' || placement === 'top') {
      const overflowsBottom = menuRect.bottom > viewportHeight - 8
      const hasSpaceAbove = triggerRect.top > menuRect.height + 8

      if (placement === 'bottom' && overflowsBottom && hasSpaceAbove) {
        // Flip to top
        menu.classList.remove('top-full', 'mt-2')
        menu.classList.add('bottom-full', 'mb-2')
      } else if (placement === 'top' && menuRect.top < 8) {
        // Flip to bottom
        menu.classList.remove('bottom-full', 'mb-2')
        menu.classList.add('top-full', 'mt-2')
      } else if (placement === 'bottom') {
        menu.classList.add('top-full')
      } else {
        menu.classList.add('bottom-full')
      }
    }
  }

  _updateButtonText(text) {
    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = text
    }
  }

  // ==========================================================================
  // PRIVATE: KEYBOARD NAVIGATION
  // ==========================================================================
  // Note: Navigation is inline rather than using shared/keyboard_controller because
  // it has menu-specific requirements: filtering out submenu items, focus management
  // on open, and integration with selection. The 30-40 lines of navigation code
  // here are simpler than configuring the generic controller for these edge cases.

  /**
   * Navigate through menu items
   * @param {number} direction - 1 for next, -1 for previous
   */
  _navigate(direction) {
    const items = this._getNavigableItems()
    if (items.length === 0) return

    // Find currently focused item
    const currentFocused = document.activeElement
    const currentIndex = items.indexOf(currentFocused)

    let nextIndex
    if (currentIndex === -1) {
      // No item focused, start from beginning or end
      nextIndex = direction > 0 ? 0 : items.length - 1
    } else {
      // Wrap around
      nextIndex = (currentIndex + direction + items.length) % items.length
    }

    items[nextIndex].focus({ preventScroll: true })
  }

  /**
   * Get all navigable menu items (top-level items only)
   * Excludes items inside submenu panels - submenus have their own navigation
   * (Right arrow to enter submenu, Left/Escape to exit)
   * @returns {HTMLElement[]} Array of focusable menu items
   */
  _getNavigableItems() {
    const allItems = Array.from(
      this.menuTarget.querySelectorAll(
        '[role="menuitem"]:not([disabled]):not([aria-disabled="true"]):not(.hidden), ' +
        '[role="menuitemcheckbox"]:not([disabled]):not([aria-disabled="true"]):not(.hidden), ' +
        '[role="menuitemradio"]:not([disabled]):not([aria-disabled="true"]):not(.hidden), ' +
        '[role="option"]:not([disabled]):not([aria-disabled="true"]):not(.hidden), ' +
        'button:not([disabled]):not([aria-disabled="true"]):not(.hidden), ' +
        'a:not([disabled]):not([aria-disabled="true"]):not(.hidden)'
      )
    )

    // Filter out ALL items inside submenu panels
    // Submenus have their own keyboard navigation via submenu controller
    // Parent menu should only navigate through top-level items
    return allItems.filter(item => {
      const submenuPanel = item.closest(`.submenu-panel, [data-${this._submenuIdentifier()}-target="panel"]`)
      return !submenuPanel
    })
  }

  // The submenu panel's target attribute is rendered by the SEPARATE submenu
  // controller, which shares this menu controller's consumer-configured
  // stimulus_namespace (a single global setting - see
  // RapidRailsUI::Configuration#stimulus_namespace). Derive its identifier
  // from our own ("menu" -> "submenu", "rui--menu" -> "rui--submenu") rather
  // than hardcoding "submenu", which only matched bare/unnamespaced consumers.
  _submenuIdentifier() {
    return this.identifier.replace(/menu$/, "submenu")
  }

  // ==========================================================================
  // PRIVATE: HOVER BEHAVIOR
  // ==========================================================================

  _setupHoverBehavior() {
    this._mouseIsOverMenu = false
    this._mouseIsOverTrigger = false

    this._boundHandlers.triggerEnter = () => {
      this._mouseIsOverTrigger = true
      this._clearHoverTimeout()
      this._hoverTimeout = setTimeout(() => this.open(), this.constructor.HOVER_OPEN_DELAY)
    }

    this._boundHandlers.triggerLeave = () => {
      this._mouseIsOverTrigger = false
      if (!this._mouseIsOverMenu) {
        this._clearHoverTimeout()
        this._hoverTimeout = setTimeout(() => this.close(), this.constructor.HOVER_CLOSE_DELAY)
      }
    }

    this._boundHandlers.menuEnter = () => {
      this._mouseIsOverMenu = true
      this._clearHoverTimeout()
    }

    this._boundHandlers.menuLeave = () => {
      this._mouseIsOverMenu = false
      if (!this._mouseIsOverTrigger) {
        this._clearHoverTimeout()
        this._hoverTimeout = setTimeout(() => this.close(), this.constructor.HOVER_CLOSE_DELAY)
      }
    }

    this.triggerTarget.addEventListener("mouseenter", this._boundHandlers.triggerEnter)
    this.triggerTarget.addEventListener("mouseleave", this._boundHandlers.triggerLeave)
    this.menuTarget.addEventListener("mouseenter", this._boundHandlers.menuEnter)
    this.menuTarget.addEventListener("mouseleave", this._boundHandlers.menuLeave)
  }

  _clearHoverTimeout() {
    if (this._hoverTimeout) {
      clearTimeout(this._hoverTimeout)
      this._hoverTimeout = null
    }
  }

  _cleanupHoverListeners() {
    if (this._boundHandlers.triggerEnter) {
      this.triggerTarget.removeEventListener("mouseenter", this._boundHandlers.triggerEnter)
      this.triggerTarget.removeEventListener("mouseleave", this._boundHandlers.triggerLeave)
      this.menuTarget.removeEventListener("mouseenter", this._boundHandlers.menuEnter)
      this.menuTarget.removeEventListener("mouseleave", this._boundHandlers.menuLeave)
    }
    this._boundHandlers = {}
  }
}
